/**
 * @fileoverview 市场数据状态管理
 * @description 管理市场列表、筛选条件、当前选中市场等状态
 * 已接入后端 API：从 /api/v1/market/marketList 获取真实市场数据
 * MVC 架构：Store 层，管理市场数据状态，协调 API 调用
 * @module stores/useMarketStore
 */

import { create } from 'zustand';
import { fetchMarketList, fetchPriceSet, mapBackendMarket } from '../services/marketService';
import { API_OK } from '../services/api';
import type { Market, MarketCategory, MarketStatus } from '../types';
import i18n from '../locales/i18n';

/** 将前端语言 key 转换为服务器 languageCode */
function getServerLanguageCode(): string {
  const lang = i18n.language || localStorage.getItem('augurx_language') || 'en';
  if (lang.startsWith('zh')) return 'zh-hant';
  return 'en';
}

// ============================================================
// Store 类型定义
// ============================================================

/**
 * 市场筛选条件接口
 */
interface MarketFilters {
  /** 分类筛选 */
  category: MarketCategory;
  /** 状态筛选 */
  status: MarketStatus | 'all';
  /** 搜索关键词 */
  keyword: string;
  /** 排序方式 */
  sortBy: 'volume' | 'endTime' | 'newest' | 'liquidity';
}

/**
 * 市场状态接口
 */
interface MarketState {
  /** 所有市场列表 */
  markets: Market[];
  /** 当前筛选条件 */
  filters: MarketFilters;
  /** 是否加载中 */
  isLoading: boolean;
  /** 加载错误信息 */
  error: string | null;
  /** 当前查看的市场详情 */
  currentMarket: Market | null;
  /** 实时价格集合（代币符号 → 价格） */
  priceSet: Record<string, number>;
  /** 上次价格更新时间 */
  priceLastUpdated: number;
}

/**
 * 市场操作接口
 */
interface MarketActions {
  /**
   * 从后端加载市场列表
   * @param page - 页码，从 0 开始
   */
  fetchMarkets: (page?: number) => Promise<void>;

  /**
   * 根据 ID 获取市场详情（先从本地列表查找，找不到再请求后端）
   * @param id - 市场 ID
   */
  fetchMarketById: (id: string) => Promise<void>;

  /**
   * 更新筛选条件
   * @param filters - 部分筛选条件
   */
  setFilters: (filters: Partial<MarketFilters>) => void;

  /**
   * 重置筛选条件为默认值
   */
  resetFilters: () => void;

  /**
   * 根据当前筛选条件获取过滤后的市场列表
   * @returns 过滤并排序后的市场列表
   */
  getFilteredMarkets: () => Market[];

  /**
   * 刷新实时价格集合
   */
  refreshPrices: () => Promise<void>;
}

type MarketStore = MarketState & MarketActions;

/** 默认筛选条件 */
const defaultFilters: MarketFilters = {
  category: 'all',
  status: 'all',
  keyword: '',
  sortBy: 'volume',
};

// ============================================================
// Store 实现
// ============================================================

/**
 * 市场数据状态 Store
 */
export const useMarketStore = create<MarketStore>()((set, get) => ({
  // 初始状态
  markets: [],
  filters: defaultFilters,
  isLoading: false,
  error: null,
  currentMarket: null,
  priceSet: {},
  priceLastUpdated: 0,

  /**
   * 从后端加载市场列表
   * 将后端原始数据通过 mapBackendMarket 转换为前端 Market 类型
   */
  fetchMarkets: async (page = 0) => {
    set({ isLoading: true, error: null });

    try {
      const langCode = getServerLanguageCode();
      const resp = await fetchMarketList(page, langCode);

      if (resp.status === API_OK && Array.isArray(resp.data)) {
        const markets = resp.data.map(mapBackendMarket);
        // 如果当前有 currentMarket（在详情页），同步更新为新语言的版本
        const prevCurrentMarket = get().currentMarket;
        const updatedCurrentMarket = prevCurrentMarket
          ? (markets.find((m) => m.id === prevCurrentMarket.id) ?? prevCurrentMarket)
          : null;
        set({ markets, isLoading: false, currentMarket: updatedCurrentMarket });
      } else {
        set({
          isLoading: false,
          error: `加载市场失败，错误码：${resp.status}`,
        });
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : '网络请求失败';
      set({ isLoading: false, error: errMsg });
    }
  },

  /**
   * 根据 ID 获取市场详情
   * 优先从本地列表查找，避免重复请求
   */
  fetchMarketById: async (id: string) => {
    // 先从本地列表查找
    const existing = get().markets.find((m) => m.id === id);
    if (existing) {
      set({ currentMarket: existing });
      return;
    }

    // 本地没有则重新加载列表
    set({ isLoading: true });
    try {
      const langCode = getServerLanguageCode();
      const resp = await fetchMarketList(0, langCode);
      if (resp.status === API_OK && Array.isArray(resp.data)) {
        const markets = resp.data.map(mapBackendMarket);
        const target = markets.find((m) => m.id === id) || null;
        set({ markets, currentMarket: target, isLoading: false });
      } else {
        set({ currentMarket: null, isLoading: false });
      }
    } catch {
      set({ currentMarket: null, isLoading: false });
    }
  },

  /**
   * 更新筛选条件（支持部分更新）
   */
  setFilters: (filters: Partial<MarketFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  /**
   * 重置所有筛选条件
   */
  resetFilters: () => {
    set({ filters: defaultFilters });
  },

  /**
   * 根据当前筛选条件过滤并排序市场列表
   * 包含分类、状态、关键词搜索和排序逻辑
   */
  getFilteredMarkets: () => {
    const { markets, filters } = get();
    let result = [...markets];

    // 分类筛选
    // high_freq: 筛选 marketType === 'high_freq'（对应 classType=0）
    // 其他分类: 筛选 category === group 字段山
    if (filters.category !== 'all') {
      if (filters.category === 'high_freq') {
        result = result.filter((m) => m.marketType === 'high_freq');
      } else {
        result = result.filter((m) => m.category === filters.category);
      }
    }

    // 状态筛选
    if (filters.status !== 'all') {
      result = result.filter((m) => m.status === filters.status);
    }

    // 关键词搜索
    if (filters.keyword.trim()) {
      const kw = filters.keyword.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(kw) ||
          m.description.toLowerCase().includes(kw) ||
          m.assetSymbol?.toLowerCase().includes(kw)
      );
    }

    // 排序
    switch (filters.sortBy) {
      case 'volume':
        result.sort((a, b) => b.totalVolume - a.totalVolume);
        break;
      case 'endTime':
        result.sort(
          (a, b) => new Date(a.endTime).getTime() - new Date(b.endTime).getTime()
        );
        break;
      case 'newest':
        result.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        break;
       case 'liquidity':
        result.sort((a, b) => b.totalLiquidity - a.totalLiquidity);
        break;
    }
    // 休市市场排到最后（不影响其他排序逻辑）
    result.sort((a, b) => {
      const aClosed = a.status === 'closed' ? 1 : 0;
      const bClosed = b.status === 'closed' ? 1 : 0;
      return aClosed - bClosed;
    });
    return result;
  },

  /**
   * 刷新实时价格集合
   * 价格数据用于高频市场卡片展示当前价格
   */
  refreshPrices: async () => {
    // 避免频繁刷新（30秒内不重复请求）
    const { priceLastUpdated } = get();
    if (Date.now() - priceLastUpdated < 30_000) return;

    try {
      const resp = await fetchPriceSet();
      if (resp.status === API_OK && resp.data) {
        set({ priceSet: resp.data, priceLastUpdated: Date.now() });
      }
    } catch {
      // 静默失败，不影响主要功能
    }
  },
}));
